import {Controller, FieldValues, useFieldArray, UseFormSetValue, useWatch} from "react-hook-form";
import {Control, UseFormRegister} from "react-hook-form/dist/types/form";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import IconButton from "@mui/material/IconButton";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import {Autocomplete, FormLabel, MenuItem, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import {FieldErrors} from "react-hook-form/dist/types/errors";
import {WriteContractForm, WritePaybackForm} from "./WriteContract";
import {useAllDict} from "@/store/dict.c";
import CurrencyYuanOutlinedIcon from '@mui/icons-material/CurrencyYuanOutlined';
import React from "react";
import {DictPO} from "@/model/system";
import {TitleBar} from "@/widget/TitleBar/TitleBar";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";

interface PaybackFormProps<T extends FieldValues> {
    control: Control<WriteContractForm>;
    register: UseFormRegister<WriteContractForm>;
    errors: FieldErrors<WriteContractForm>;
    setValue: UseFormSetValue<any | T>;
}

const PaybackForm = <T extends FieldValues, >({control, register, errors, setValue}: PaybackFormProps<T>) => {
    const {fields, append, remove} = useFieldArray({
        name: "paybacks",
        control,
        keyName: "_id"
    });
    const {receivingMethod, paymentMethod, boole} = useAllDict()

    const flag = useWatch({
        control,
        name: fields.map((field, index) => `paybacks.${index}.flag`) as any,
    });

    return (
        <>
            <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignContent={"center"}
                alignItems={'center'}
            >
                <TitleBar title={"回款信息"} extra={<IconButton
                    size={"small"}
                    onClick={() => append({flag: 1} as WritePaybackForm)}
                    color={'default'}
                    sx={{padding: 0}}>
                    <ControlPointOutlinedIcon/>
                </IconButton>}/>
            </Stack>
            {fields.map((field, index) => {
                let flagLabel = ""
                if (flag[index] == 0) {
                    flagLabel = "预计"
                }
                return (
                    <Stack
                        width={'100%'}
                        key={field._id}
                        direction={"row"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        alignContent={"center"}
                    >
                        <Grid spacing={2} container width={'inherit'}>

                            <Grid size={6}>
                                <FormControl fullWidth={true} required={true}>
                                    <FormLabel>产品名称</FormLabel>
                                    <TextField
                                        error={!!errors?.paybacks?.[index]?.productName}
                                        helperText={errors?.paybacks?.[index]?.productName?.message}
                                        {...register(`paybacks.${index}.productName`)}
                                        placeholder="填写产品名称"
                                        variant="outlined"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={6}>
                                <FormControl fullWidth={true}>
                                    <FormLabel>是否已收款</FormLabel>
                                    <Controller control={control} render={({field: {onChange, value}}) => {
                                        return <TextField value={value} onChange={event => onChange(event.target.value)}
                                                          placeholder={"选择是否收款"} select={true}>
                                            {boole?.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    }} name={`paybacks.${index}.flag`}/>
                                </FormControl>
                            </Grid>
                            <Grid size={6}>
                                <FormControl fullWidth={true} autoFocus={true} required={true}>
                                    <FormLabel>回款金额</FormLabel>
                                    <Controller control={control} render={({field: {onChange, value}}) => <TextField
                                        error={!!errors?.paybacks?.[index]?.paymentMoney}
                                        helperText={errors?.paybacks?.[index]?.paymentMoney?.message}
                                        type={'number'}
                                        value={value}
                                        onChange={event => onChange(event.target.value)}
                                        slotProps={{
                                            input: {
                                                inputProps: {
                                                    min: 0,
                                                    max: 1000000,
                                                    maxLength: 8, inputMode: 'numeric', pattern: '/^[0-9.]*$/'
                                                },
                                                endAdornment: <CurrencyYuanOutlinedIcon color="disabled"/>,
                                            },
                                        }}
                                        placeholder="填写回款金额"
                                        variant="outlined"
                                    />} name={`paybacks.${index}.paymentMoney`}/>
                                </FormControl>
                            </Grid>
                            <Grid size={6}>
                                <FormControl fullWidth={true} autoFocus={true} required={true}>
                                    <FormLabel>{flagLabel}回款时间</FormLabel>
                                    <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                                        <DatePicker
                                            showYearDropdown={true}
                                            showMonthDropdown={true}
                                            dateFormat="yyyy/MM/dd"
                                            selected={value} onChange={date => onChange(date)}
                                            locale='zhCN'
                                            placeholderText={"选择回款时间"}
                                            customInput={<TextField
                                                error={!!errors?.paybacks?.[index]?.paymentDate}
                                                helperText={errors?.paybacks?.[index]?.paymentDate?.message}
                                                sx={{
                                                    width: "100%",
                                                    "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                                }}
                                            />}
                                        />
                                    </DatePickers>} name={`paybacks.${index}.paymentDate`}/>
                                </FormControl>
                            </Grid>
                            <Grid size={6}>
                                <FormControl fullWidth={true} required={true}>
                                    <FormLabel>{flagLabel}回款方式</FormLabel>
                                    <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                                        options={paymentMethod}
                                        value={value as any}
                                        onChange={(_, option: DictPO | null) => {
                                            setValue(`paybacks.${index}.paymentMethodId`, option?.value);
                                            onChange(option?.label)
                                        }}
                                        fullWidth
                                        renderInput={(params) => (
                                            <TextField error={!!errors?.paybacks?.[index]?.paymentMethod}
                                                       helperText={errors?.paybacks?.[index]?.paymentMethod?.message}
                                                       variant="outlined"
                                                       placeholder={"选择回款方式"} {...params} />
                                        )}
                                    />} name={`paybacks.${index}.paymentMethod`}/>
                                </FormControl>
                            </Grid>
                            <Grid size={6}>
                                <FormControl fullWidth={true} required={true}>
                                    <FormLabel>到账方式</FormLabel>
                                    <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                                        options={receivingMethod}
                                        value={value as any}
                                        onChange={(_, option: DictPO | null) => {
                                            setValue(`paybacks.${index}.receivingMethodId`, option?.value);
                                            onChange(option?.label)
                                        }}
                                        fullWidth
                                        renderInput={(params) => (
                                            <TextField error={!!errors?.paybacks?.[index]?.receivingMethod}
                                                       helperText={errors?.paybacks?.[index]?.receivingMethod?.message}
                                                       variant="outlined"
                                                       placeholder={"选择到账方式"} {...params} />
                                        )}
                                    />} name={`paybacks.${index}.receivingMethod`}/>
                                </FormControl>
                            </Grid>
                            <Grid size={12}>
                                <FormControl fullWidth={true}>
                                    <FormLabel>备注</FormLabel>
                                    <TextField
                                        multiline
                                        {...register(`paybacks.${index}.remark`)}
                                        rows={3}
                                        slotProps={{
                                            input: {
                                                sx: {height: 80},
                                                inputProps: {maxLength: 250},
                                            },
                                        }}
                                        placeholder="输入备注"
                                        fullWidth
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <IconButton
                            onClick={() => remove(index)}
                            sx={{padding: 0}}
                            size={"small"}>
                            <RemoveCircleOutlineOutlinedIcon color={"error"}/>
                        </IconButton>
                    </Stack>
                );
            })}
        </>
    );
};

export default PaybackForm;
